import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { View, TouchableOpacity } from 'react-native'
import Icon from '../Icons'
import Txt from '../Txt'
import { useTheme } from '../../theming/ThemeProvider'
import { parseNumberWithThousandSeparator, getDateFormat, getCardsLocationText } from '../../tools/dataParse'
import { usePlatforms } from '../../hooks/usePlatforms'
import Identity from '../../utils/Identity'
import LocationInformation from './LocationsInformation'
import useStyles from './useStyles'
import { initSelector } from '../../redux/initReducer/initSelector'
import { loadDetailSelector } from '../../redux/loadDetails/loadDetailsSelector'

const getEquipment = (equipmentTypes, id) => {
  const equipment = equipmentTypes && equipmentTypes.find(eq => eq.equipment_code === id)
  return equipment ? equipment.description : '--'
}

const getIsApproved = (array = []) => array.length ? array[array.length - 1]?.offering_status === 'A' : false

const LoadCard = ({ item, handleFavorite, handleDetail, isHotOffer, isHistory, hasLabel = false }) => {
  const { isTablet, isDesktop } = usePlatforms()
  const {
    origin, destination, marked, rate, movementNumber, order_status,
    equipmentType: id, weight, stopsCount, orderNumber, offer_history
  } = item
  const [isMark, setIsMark] = useState(!!marked)
  const { loadDetails } = useSelector(loadDetailSelector)

  const { theme } = useTheme()
  const styles = useStyles(theme, isTablet, isDesktop, hasLabel)

  const { equipmentTypes } = useSelector(initSelector)

  const isApproved = useMemo(() => isHistory ? getIsApproved(offer_history) : false, [isHistory, offer_history])

  const statusColor = useMemo(() => isHistory
    ? isApproved ? theme.FERN : theme.RED_ORANGE
    : order_status ? order_status === 'BID' ? theme.GOLDEN_GRASS : theme.LOCHMARA : 'transparent', [isApproved, isHistory, order_status, theme])

  const Status = useMemo(() => isHistory ? (
    <View
      style={[
        styles.status,
        { backgroundColor: statusColor }
      ]}
    >
      <Txt
        color='WHITE_TEXT'
        text={isApproved ? 'Accepted' : 'Rejected'}
        identity='body3Bold'
      />
    </View>
  ) : <></>, [isApproved, isHistory, statusColor, styles.status])

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      style={[
        styles.cardStyle,
        Identity.Shadow(theme).Card,
        isHotOffer && styles.hotOfferCard,
        loadDetails && loadDetails.load && loadDetails.load.orderNumber === orderNumber && styles.selected
      ]}
      onPress={() => handleDetail(item)}
    >
      {isTablet && Status}
      <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
        <View style={styles.location}>
          <LocationInformation
            origin={getCardsLocationText(origin)}
            destination={getCardsLocationText(destination)}
            originText={origin?.arrivals && origin.arrivals[0] && origin.arrivals[0].date ? getDateFormat(origin?.arrivals[0]) : '- -'}
            destinationText={destination?.arrivals && destination.arrivals[0] && destination.arrivals[0].date ? getDateFormat(destination?.arrivals[0]) : '- -'}
            stopsCount={stopsCount}
            showStops
          />
        </View>
        <View style={styles.information}>
          {!isTablet && Status}
          <View style={styles.detail}>
            {isTablet && (
              <Icon
                fill={theme.BUNKER}
                name='Tractors'
                height={24}
                width={24}
              />
            )}
            <Txt
              color={isTablet ? 'OUTER_SPACE' : 'SLATE_GRAY'}
              text={getEquipment(equipmentTypes, id)}
              identity={isTablet ? 'body2' : 'body3'}
              style={[styles.Text, { marginBottom: isDesktop ? 0 : 5 }]}
            />
          </View>
          {!isTablet && (
            <Txt
              color='GOLDEN_GRASS'
              text={`${stopsCount || 0} stops`}
              identity='body'
              style={[styles.Text, { marginBottom: isDesktop ? 0 : 5 }]}
            />
          )}
          <View style={styles.detail}>
            {isTablet && (
              <Icon
                fill={theme.BUNKER}
                name='Atlas'
                height={24}
                width={24}
              />
            )}
            <Txt
              color='OUTER_SPACE'
              text={weight ? `${parseNumberWithThousandSeparator(weight)} Lbs` : '- -'}
              identity={isTablet ? 'body2' : 'body2Bold'}
              style={[styles.Text, { marginBottom: isDesktop ? 0 : 5 }]}
            />
          </View>
          <View style={styles.detail}>
            {isTablet && (
              <Icon
                fill={theme.BUNKER}
                name='Price'
                height={24}
                width={24}
              />
            )}
            <Txt
              color='FERN'
              text={`$${rate || ' --'} USD`}
              identity={isTablet ? 'body2' : 'body2Bold'}
              style={styles.Text}
            />
          </View>
        </View>
      </View>
      {!isHistory ? isHotOffer ? (
        <View style={[styles.iconContainer, styles.hotOffer]}>
          <Icon
            fill={theme.RED_ORANGE}
            name='HotOffer'
            height={isDesktop ? 20 : 25}
            width={isDesktop ? 20 : 25}
          />
        </View>
      ) : (
        <TouchableOpacity
          activeOpacity={0.8}
          style={[
            styles.iconContainer,
            { order: isDesktop ? -1 : 0 }
          ]}
          onPress={() => {
            setIsMark(!isMark)
            handleFavorite({ movementNumber, isMark: !!marked })
          }}
        >
          <Icon
            fill={isMark ? 'yellow' : theme.GEYSER}
            name={isMark ? 'Shape' : 'Star'}
            height={isDesktop ? 20 : 18}
            width={isDesktop ? 20 : 18}
          />
        </TouchableOpacity>
      ) : <></>}
      <View style={[styles.marked, (!isTablet || !isHistory) && { backgroundColor: statusColor }]} />
    </TouchableOpacity>
  )
}

LoadCard.propTypes = {
  item: PropTypes.object.isRequired,
  handleDetail: PropTypes.func.isRequired,
  handleFavorite: PropTypes.func.isRequired,
  isHotOffer: PropTypes.bool,
  isHistory: PropTypes.bool,
  hasLabel: PropTypes.bool
}

function areEqual (prevProps, nextProps) {
  return JSON.stringify(prevProps.item) === JSON.stringify(nextProps.item)
}

export default React.memo(LoadCard, areEqual)
